<template>
  <div>
    <!-- header/hero (unchanged) -->
    <section class="news d-flex align-items-center">
      <div class="container py-5"></div>
    </section>

    <section class="py-4">
      <div class="container">
        <div class="p-4">
          <h5 class="fw-bold mb-2"><span style="color:var(--brand)">NEWS</span></h5>
          <p class="mb-4" style="padding-bottom: 26px;">The latest news from the Richview Engineering Team</p>

          <!-- TOP: Element Plus pagination (includes page size chooser) -->
          <div class="d-flex flex-column flex-md-row align-items-center justify-content-between mb-3 gap-2">
            <div class="d-flex align-items-center gap-2">
              <div v-if="loading" class="ms-3 small">Loading...</div>
              <div v-if="totalItems > 0" class="small">Showing {{ startItem }}–{{ endItem }} of {{ totalItems }}</div>            
            </div>

            <div class="mt-2 mt-md-0">
              <el-pagination
                v-if="totalItems > 0"
                :current-page="page"
                :page-size ="pageSize"
                :page-sizes="[10,20,50]"
                :total="totalItems"
                layout="sizes, prev, pager, next, jumper"
                small
                @current-change="handleCurrentChange"
                @size-change="handleSizeChange"
              />
            </div>
          </div>

          <!-- news list (same visual style as home) -->
          <div class="list-group mb-3">
            <div v-if="error" class="alert alert-danger py-2">Failed to load news.</div>
            <template v-else>
              <div
                v-for="item in items"
                :key="item.newsId"
                class="list-group-item bg-transparent border-0 px-0 py-3 news-entry"
              >
                <!-- title only is clickable -->
                <div class="d-flex w-100 justify-content-between align-items-baseline">
                  <h6 class="mb-0 news-title">
                    <a href="#" class="news-title-link" @click.prevent="goToNewsDetail(item.newsId)">
                      {{ item.mainTitle }}
                    </a>
                  </h6>
                </div>

                <!-- subtitle -->
                <div class="news-subtitle mt-2">
                  {{ item.subTitle }}
                </div>

                <!-- content / excerpt -->
                <div class="news-content">
                  {{ item.content }}
                </div>

                <!-- footer row: date placed on the bottom-left -->
                <div class="news-footer mt-3">
                  
                  <small class="news-date">
                    <div class="d-flex justify-content-start gap-1">
                      <div style="margin-top: 1px; padding-right: 2px;"><el-icon><Calendar /></el-icon></div>
                      <div>{{ formatDate(item.strPublishDate) }}</div>
                    </div>
                  </small>
                </div>
              </div>
 
               <div v-if="items.length === 0 && !loading" class="small">No news available.</div>
             </template>
          </div>

          <!-- BOTTOM: Element Plus pagination (mirrors top) -->
          <div class="d-flex flex-column flex-md-row align-items-center justify-content-end gap-1">
            <div class="pagination-wrap mt-2 mt-md-0">
              <el-pagination
                v-if="totalItems >= pageSize && pageSize > 10"
                :current-page="page"
                :page-size="pageSize"
                :page-sizes="[10,20,50]"
                :total="totalItems"
                layout="sizes, prev, pager, next, jumper"
                small
                @current-change="handleCurrentChange"
                @size-change="handleSizeChange"
              />
            </div>
          </div>

        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue';
import { MONTHS } from '@/constants';
import apiClient from '@/apiClient';
import { useRouter } from 'vue-router';
import { TIPSTAG_ERROR } from '@/constants';

const { proxy } = getCurrentInstance();

/* reactive state */
const items = ref([]);
const totalItems = ref(0);
const page = ref(1);
const pageSize = ref(10);
const loading = ref(false);
const error = ref(false);

// const totalPages = computed(() => Math.max(1, Math.ceil(totalItems.value / pageSize.value)));
const startItem = computed(() => (items.value.length ? (page.value - 1) * pageSize.value + 1 : 0));
const endItem = computed(() => Math.min(totalItems.value, (page.value - 1) * pageSize.value + items.value.length));


/* handlers wired to el-pagination */
function handleCurrentChange(newPage) {
  page.value = newPage;
  loadNewsPagingData(newPage, pageSize.value);
}
function handleSizeChange(newSize) {
  pageSize.value = newSize;
  page.value = 1; // reset to first page
  loadNewsPagingData(1, pageSize.value);
}

/* reload when pageSize changes from elsewhere */
watch(pageSize, () => loadNewsPagingData(1, pageSize.value));

onMounted(() => { loadNewsPagingData(page.value, pageSize.value); });

/* date formatter */
function formatDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  return MONTHS[dt.getMonth()] + ' ' + dt.getDate() + ', ' + dt.getFullYear();  
}


async function loadNewsPagingData(pageIndex, pageSize) {
  
  page.value = pageIndex;
  
  try {
    const response = await apiClient.post('/Main/getNewsList', {
      pageIndex: pageIndex,
      pageSize: pageSize
    });
    if(response && response.data) {
      items.value = response.data.news || [];
      totalItems.value = response.data.total || 0;
    } 
  } catch (e) {
    if(e.response && e.response.data && e.response.status === 400) {    
     proxy.$handleTips(e.response.data.tag, e.response.data.msg, e);
   } else {
    proxy.$handleTips(TIPSTAG_ERROR, 'Network error occurred. Please try again later.', e);
  } 
  }
}

const router = useRouter();
function goToNewsDetail(newsId) {
  
  router.push({ name: 'NewsDetail', params: { newsId: newsId } });
}


</script>


<style scoped>
/* keep news colors consistent with homepage */
.news-title { margin: 0; font-weight: 600; color: rgba(255,255,255,0.96); }
.news-subtitle { color: rgba(255,255,255,0.72); font-size: 0.95rem; line-height: 1.45; margin-top: .35rem; }
.news-content { color: rgba(255,255,255,0.72); margin-top: 1.6rem; }

/* move publish date to bottom-left */
.news-footer {
  display: flex;
  justify-content: flex-start; /* left aligned */
  align-items: center;
  margin-top: 12px;
}
.news-date {
  color: rgba(255,255,255,0.62);
  font-size: 0.85rem;
  margin: 0;
}

/* list visuals (match homepage) */
.list-group-item { background: transparent; border-bottom: 1px solid rgba(255,255,255,0.03); }
.list-group-item:last-child { border-bottom: none; }
.news-entry:hover { background: transparent; text-decoration: none; }

/* center Element Plus pagination and make it compact like the screenshot */
.pagination-wrap { display: flex; justify-content: center; align-items: center; }
.el-pagination {
  color: rgba(255,255,255,0.88);
  background: transparent;
  --el-font-size: 13px;
}

/* pager: make the page-number pills dark and numbers bright white to match page bg */
.el-pagination .el-pager {
  background: transparent;
  padding: 2px 6px;
  border-radius: 8px;
}

/* page number pills */
.el-pagination .el-pager li {
  background: rgba(6,18,28,0.65);           /* deep, site-matching surface */
  border: 1px solid rgba(255,255,255,0.04); /* faint edge for separation */
  color: #ffffff !important;                /* bright white numbers */
  min-width: 34px;
  height: 34px;
  line-height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0 6px;
  border-radius: 6px;
  transition: background .12s ease, transform .08s ease;
}

/* hover state: slight lift + subtle lighten */
.el-pagination .el-pager li:hover {
  background: rgba(255,255,255,0.04);
  transform: translateY(-2px);
  color: #ffffff !important;
}

/* active page: use brand tint but keep text white */
.el-pagination .el-pager li.active,
.el-pagination .el-pager li.active a {
  background: rgba(13,110,253,0.18);  /* soft brand tint */
  border-color: rgba(13,110,253,0.28);
  color: #ffffff !important;
  box-shadow: 0 8px 20px rgba(13,110,253,0.06);
}

/* prev/next icons: match white tone */
.el-pagination .el-icon,
.el-pagination .el-pager li i {
  color: rgba(255,255,255,0.92);
}

/* keep selects / inputs dark & consistent */
.el-pagination .el-select .el-input__inner,
.el-pagination .el-input__inner {
  background: rgba(6,18,28,0.55);
  border: 1px solid rgba(255,255,255,0.04);
  color: rgba(255,255,255,0.92);
  border-radius: 6px;
}

/* responsive size tweak */
@media (max-width: 767.98px) {
  .el-pagination .el-pager li { min-width: 28px; height: 30px; margin: 0 4px; line-height: 30px; }
}

/* ensure only title link is styled and not full-item clickable */
.news-title-link {
  color: inherit;
  text-decoration: none;
  transition: color .12s ease, transform .08s ease;
}
.news-title-link:hover {
  color: #fff;
  transform: translateX(3px);
  text-decoration: none;
}
</style>